import { useInfiniteQuery, useQuery } from 'react-query'
import QUERIES from 'api/queries'
import { useMemo, useRef } from 'react'
import { IGetTransactionListParams, ITransaction } from './models'
import { getTransactionById, getTransactions, getTransactionStatistic } from './services'
import queries from 'api/queries'
import { useIntegrations } from 'api/integrations/queries'
import { TransactionType } from './dtos'
import { useLocalization } from 'localization'

export const useTransactions = (params: IGetTransactionListParams, enabled = true) => {
	const { data: integrations = [], isFetched } = useIntegrations()
	const totalItemsRef = useRef<number>(0)
	const filteredItemsRef = useRef<number>(0)
	const query = useInfiniteQuery(
		[QUERIES.transactions, params],
		async ({ pageParam = 0 }) => {
			const response = await getTransactions({
				...params,
				page: pageParam,
			})

			response.items = response.items.map(item => ({
				...item,
				integration: integrations.find(({ id }) => id === item.integrationId),
			}))

			totalItemsRef.current = response.totalItems
			filteredItemsRef.current = response.filteredItems

			return response
		},
		{
			cacheTime: 1000,
			staleTime: 1000,
			enabled: isFetched && enabled,
			getNextPageParam: ({ page, pageSize, filteredItems }) => {
				return (page + 1) * pageSize < filteredItems ? page + 1 : undefined
			},
		}
	)

	const transactions = useMemo(
		() => query.data?.pages.reduce((memo, next) => [...memo, ...next.items], [] as ITransaction[]) || [],
		[query.data]
	)

	return {
		...query,
		transactions,
		totalItems: totalItemsRef.current,
		filteredItems: filteredItemsRef.current,
	}
}

export const useTransactionStatistic = () => {
	const { t } = useLocalization()
	const query = useQuery(queries.transactionStatistic, getTransactionStatistic)

	return {
		...query,
		userTransactionsTypeOptions: useMemo(
			() =>
				query.data
					? Object.keys(query.data.transactions)
							.filter(key => query.data.transactions[key as TransactionType] > 0)
							.map(key => ({
								value: key,
								label: t(`transactions.types.${key}`),
							}))
					: [],
			[query.data, t]
		),
	}
}

export const useTransactionById = (id?: ITransaction['id']) =>
	useQuery([queries.transactionById, id], () => (id ? getTransactionById(id) : undefined), {
		enabled: !!id,
	})
